/* @flow */
import {fetchList} from 'nutshell-core/entities/fetch-list';
import {transformDataForReport} from 'nutshell-core/json-to-report-data';
import {safelyParseError} from 'nutshell-core/utils';
import type {ActionsObservable} from 'redux-observable';
import {Observable} from 'rxjs';
import {switchMap, merge, filter, map, catchError} from 'rxjs/operators';

import {
    getEngagementReportPostParams,
    type EditionEngagementReportPostParams,
} from './edition-utils';
import {
    updateEditionRecipientListData,
    failEditionRecipientListData,
    updateEditionEngagementReportTableData,
    updateEditionEngagementReportChartData,
    failEditionEngagementReportChartData,
    type EditionEngagementReportDataRequestedAction,
    type EditionRecipientListRequestedAction,
} from './editions-actions';

export const requestEditionRecipientListEpic = (action$: ActionsObservable<*>) =>
    action$.ofType('EDITION_RECIPIENT_LIST_DATA_REQUESTED').pipe(
        switchMap((action: EditionRecipientListRequestedAction) => {
            const {filters, pageNum = 1, columns, sort, q} = action.payload;

            // Our pagination components aren't 0-based, but this specific
            // request expects page 0 to be the first page. Using Math.max() here
            // in case older URLs with page 0 are still in the wild
            const adjustedPageNum = Math.max(0, pageNum - 1);

            const listRequestStream = Observable.from(
                fetchList('contacts', {
                    filter: filters,
                    sort,
                    page: {page: adjustedPageNum},
                    fields: columns,
                    q,
                })
            );

            const editionPeopleListDataStream = listRequestStream.pipe(
                map((response) => updateEditionRecipientListData(response))
            );

            return editionPeopleListDataStream.pipe(
                catchError((err) => {
                    const safeError = safelyParseError(err);

                    return Observable.of(failEditionRecipientListData(safeError));
                })
            );
        })
    );

export const requestEditionEngagementReportDataEpic = (action$: ActionsObservable<*>) =>
    action$.ofType('EDITION_ENGAGEMENT_REPORT_DATA_REQUESTED').pipe(
        switchMap((action: EditionEngagementReportDataRequestedAction) => {
            const {reportParams, filters} = action.payload;
            const params = getEngagementReportPostParams(reportParams, filters);

            const requestStream = Observable.from(fetchData(params));
            const reportDataStream = requestStream.pipe(map(transformDataForReport));

            const reportTableDataStream = reportDataStream.pipe(
                map(({tableData}) => updateEditionEngagementReportTableData(tableData))
            );

            const reportChartDataStream = reportDataStream.pipe(
                map((data) => data.chartData),
                filter((chartData) => Boolean(chartData)),
                map((chartData) => updateEditionEngagementReportChartData({chartData}))
            );

            return reportChartDataStream.pipe(
                merge(reportTableDataStream),
                catchError((err) => Observable.of(failEditionEngagementReportChartData(err)))
            );
        })
    );

function fetchData(params: EditionEngagementReportPostParams) {
    return Promise.all([fetchEditionEngagementReportData(params)]);
}

function fetchEditionEngagementReportData(params: EditionEngagementReportPostParams): Promise<any> {
    const url = '/rest/editions/report';
    const ajaxParams = {
        url,
        dataType: 'json',
        data: params,
    };

    return ($.ajax(ajaxParams): any); // This is how we have to convince flow that the response is a ReportsResponse
}
